/**
 * PURPOSE: Modal de configuración para descargar reporte estadístico/general por deporte.
 * RESPONSIBILITIES:
 * - Permitir elegir fechas/fases desde opciones del backend.
 * - Permitir activar/desactivar secciones del reporte.
 * - Descargar PDF mostrando loading profesional durante generación.
 * COLLABORATORS:
 * - sportReportsService.js
 * - InstitucionesArea.jsx
 * NOTES:
 * - El modal no conoce reglas de deporte; renderiza defaults y opciones resueltas por strategy backend.
 * - Respeta defaults explícitos: Handball puede abrir con todo seleccionado y otros deportes con ninguna fecha.
 * ROUTE: src/area/InstitucionesArea/ModalSportStatisticalReport.jsx
 */

import React, { useEffect, useMemo, useState } from 'react'
import { FiAlertCircle, FiCheckSquare, FiDownloadCloud, FiFileText, FiX } from 'react-icons/fi'
import {
    downloadSportStatisticalReportPdf,
    triggerBlobDownload,
} from './sportReportsService.js'

const PRIMARY = '#1e88e5'

const Spinner = ({ className = 'h-4 w-4' }) => (
    <svg
        className={`${className} animate-spin`}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
)

function buildDefaultSections(sectionOptions = []) {
    return sectionOptions.reduce((acc, section) => {
        acc[section.key] = section.defaultSelected !== false
        return acc
    }, {})
}

const ModalSportStatisticalReport = ({ open, onClose, idevent, idsport, options }) => {
    const [selectedStageKeys, setSelectedStageKeys] = useState([])
    const [sections, setSections] = useState({})
    const [downloading, setDownloading] = useState(false)
    const [error, setError] = useState('')

    const stages = useMemo(() => options?.stages || [], [options])
    const sectionOptions = useMemo(() => options?.sections || [], [options])
    const requireStagesForDownload = options?.ui?.requireStagesForDownload !== false
    const stageHint = options?.ui?.stageHint || 'Por defecto se incluyen todas las fechas/fases disponibles.'

    useEffect(() => {
        if (!open || !options) return

        const hasExplicitStageDefaults = Array.isArray(options?.defaults?.selectedStageKeys)
        const nextStageKeys = hasExplicitStageDefaults
            ? options.defaults.selectedStageKeys
            : stages.map(stage => stage.key)

        setSelectedStageKeys(nextStageKeys)
        setSections(options?.defaults?.sections || buildDefaultSections(sectionOptions))
        setError('')
    }, [open, options, sectionOptions, stages])

    if (!open) return null

    const allStagesSelected = stages.length > 0 && selectedStageKeys.length === stages.length
    const selectedSectionsCount = Object.values(sections).filter(Boolean).length
    const stageSelectionOk = !requireStagesForDownload || selectedStageKeys.length > 0
    const canDownload = stages.length > 0 && stageSelectionOk && selectedSectionsCount > 0 && !downloading

    const toggleStage = key => {
        setSelectedStageKeys(prev => (
            prev.includes(key)
                ? prev.filter(item => item !== key)
                : [...prev, key]
        ))
    }

    const toggleAllStages = () => {
        setSelectedStageKeys(allStagesSelected ? [] : stages.map(stage => stage.key))
    }

    const toggleSection = key => {
        setSections(prev => ({ ...prev, [key]: !prev[key] }))
    }

    const handleDownload = async () => {
        if (!canDownload) {
            if (requireStagesForDownload && selectedStageKeys.length === 0) {
                setError('Selecciona al menos una fecha o fase para generar el reporte.')
            }
            return
        }

        setDownloading(true)
        setError('')

        try {
            const selectedStages = stages.filter(stage => selectedStageKeys.includes(stage.key))
            const { blob, filename } = await downloadSportStatisticalReportPdf({
                idevent,
                idsport,
                stages: selectedStages,
                sections,
            })

            triggerBlobDownload(blob, filename)
            onClose?.()
        } catch (err) {
            const message = err?.message || 'No se pudo generar el reporte.'
            setError(message)
        } finally {
            setDownloading(false)
        }
    }

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/55 px-4 py-6 backdrop-blur-sm" style={{ fontFamily: 'Quicksand' }}>
            <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
                <div className="flex items-start justify-between gap-4 border-b border-slate-100 bg-gradient-to-r from-slate-900 via-slate-800 to-[#1e88e5] px-5 py-4 text-white">
                    <div className="flex items-center gap-3">
                        <div className="grid h-11 w-11 place-items-center rounded-xl bg-white/12 ring-1 ring-white/20">
                            <FiFileText className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-sky-100">Reporte deportivo</p>
                            <h2 className="text-xl font-black leading-tight">Configurar descarga</h2>
                            <p className="text-xs text-slate-200">Selecciona fechas/fases y las secciones que deseas incluir.</p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        disabled={downloading}
                        className="rounded-xl p-2 text-white/80 transition hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                        title="Cerrar"
                    >
                        <FiX className="h-5 w-5" />
                    </button>
                </div>

                <div className="max-h-[75vh] overflow-y-auto px-5 py-5">
                    {error && (
                        <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                            <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h3 className="text-sm font-black uppercase tracking-wide text-slate-800">Selecciona fechas</h3>
                                <p className="text-xs text-slate-500">{stageHint}</p>
                            </div>
                            <button
                                type="button"
                                onClick={toggleAllStages}
                                disabled={stages.length === 0 || downloading}
                                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 shadow-sm transition hover:border-[#1e88e5] hover:text-[#1e88e5] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <FiCheckSquare className="h-4 w-4" />
                                {allStagesSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
                            </button>
                        </div>

                        {stages.length === 0 ? (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
                                No se encontraron fechas configuradas en config_category para este evento/deporte.
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-7">
                                    {stages.map(stage => {
                                        const checked = selectedStageKeys.includes(stage.key)
                                        return (
                                            <label
                                                key={stage.key}
                                                className={`flex cursor-pointer items-center justify-center rounded-xl border px-3 py-2 text-center text-sm font-black transition ${checked
                                                    ? 'border-[#1e88e5] bg-blue-50 text-[#1976d2] shadow-sm'
                                                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                                                    }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="sr-only"
                                                    checked={checked}
                                                    disabled={downloading}
                                                    onChange={() => toggleStage(stage.key)}
                                                />
                                                {stage.shortLabel || stage.label}
                                            </label>
                                        )
                                    })}
                                </div>

                                {requireStagesForDownload && selectedStageKeys.length === 0 && (
                                    <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-xs font-bold text-[#1976d2]">
                                        Selecciona una o más fechas/fases para habilitar la descarga.
                                    </div>
                                )}
                            </>
                        )}
                    </section>

                    <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="mb-3">
                            <h3 className="text-sm font-black uppercase tracking-wide text-slate-800">Incluir en el reporte</h3>
                            <p className="text-xs text-slate-500">Puedes generar un reporte completo o solo con las secciones necesarias.</p>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                            {sectionOptions.map(section => {
                                const checked = sections[section.key] !== false
                                return (
                                    <label
                                        key={section.key}
                                        className={`flex cursor-pointer gap-3 rounded-2xl border p-3 transition ${checked
                                            ? 'border-[#1e88e5] bg-blue-50/70'
                                            : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                                            }`}
                                    >
                                        <input
                                            type="checkbox"
                                            className="mt-1 h-4 w-4 accent-[#1e88e5]"
                                            checked={checked}
                                            disabled={downloading}
                                            onChange={() => toggleSection(section.key)}
                                        />
                                        <span>
                                            <span className="block text-sm font-black text-slate-800">{section.label}</span>
                                            <span className="block text-xs leading-relaxed text-slate-500">{section.description}</span>
                                        </span>
                                    </label>
                                )
                            })}
                        </div>
                    </section>
                </div>

                <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs font-semibold text-slate-500">
                        {selectedStageKeys.length} fecha(s) · {selectedSectionsCount} sección(es) seleccionada(s)
                    </p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={downloading}
                            className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={handleDownload}
                            disabled={!canDownload}
                            style={{ background: canDownload ? PRIMARY : '#93c5fd' }}
                            className="inline-flex min-w-[170px] items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-black text-white shadow-lg transition hover:brightness-95 disabled:cursor-not-allowed disabled:shadow-none"
                        >
                            {downloading ? <Spinner /> : <FiDownloadCloud className="h-4 w-4" />}
                            {downloading ? 'Generando PDF…' : 'Descargar PDF'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ModalSportStatisticalReport
